import React, { useState } from 'react';
import styled from 'styled-components';
import { CSSTransition } from 'react-transition-group';
import { renderContent } from 'utilities/content/content-utilities';
import { Modal } from '@tvg/fuse-design';
import { BnplCreditOffer, CreditOfferTypes, StcCreditOffer, Take3CreditOffer } from 'types/CreditOfferTypes';
import { sendAnalyticsEvent } from 'utilities/veryPay/veryPayUtilities';
import BnplPage from './pages/BnplPage';
import PaymentOptionsPage from './pages/PaymentOptionsPage';
import StcPage from './pages/StcPage';
import Take3Page from './pages/Take3Page';

const StyledModal = styled(Modal)`
  .fuse-modal__body {
    position: relative;
    overflow: hidden;
  }
`;

const defaultDialogBackButtonState = false;

const defaultDialogHeading = renderContent('verypay.dialog.options.dialogHeading');

const defaultDialogPageState: Record<string, boolean> = {
  paymentOptions: true,
  [CreditOfferTypes.BNPL]: false,
  [CreditOfferTypes.TAKE3]: false,
  [CreditOfferTypes.STC]: false,
};

const dialogHeadings: Record<string, string> = {
  [CreditOfferTypes.BNPL]: renderContent('verypay.dialog.bnpl.dialogHeading'),
  [CreditOfferTypes.TAKE3]: renderContent('verypay.dialog.take3.dialogHeading'),
  [CreditOfferTypes.STC]: renderContent('verypay.dialog.stc.dialogHeading'),
};

const gaLabel: Record<string, string> = {
  [CreditOfferTypes.BNPL]: 'More options - BNPL',
  [CreditOfferTypes.TAKE3]: 'More options - Take 3',
  [CreditOfferTypes.STC]: 'More options - Flex Payments',
};

const transitionDuration = 200;

type Props = {
  apr: string;
  bnpl: BnplCreditOffer;
  stc: StcCreditOffer;
  take3: Take3CreditOffer;
  nonCreditAccountUser: boolean;
  productPrice: string;
  open: boolean;
  onClose: () => void;
};

function VeryPayDialog(props: Props) {
  const { apr, bnpl, stc, take3, nonCreditAccountUser, productPrice, open, onClose } = props;
  const [showDialogBackButton, setShowDialogBackButton] = useState(defaultDialogBackButtonState);
  const [activeDialogHeading, setActiveDialogHeading] = useState(defaultDialogHeading);
  const [activePage, setActivePage] = useState(defaultDialogPageState);

  const setPage = (page: string) => {
    setShowDialogBackButton(true);
    setActiveDialogHeading(dialogHeadings[`${page}`]);
    setActivePage((previousState) => ({
      ...previousState,
      paymentOptions: false,
      [page]: true,
    }));

    sendAnalyticsEvent({
      gaLabel: gaLabel[`${page}`],
    });
  };

  const resetDialog = () => {
    setShowDialogBackButton(defaultDialogBackButtonState);
    setActiveDialogHeading(defaultDialogHeading);
    setActivePage(defaultDialogPageState);
  };

  const handleDialogBackButtonClick = () => {
    resetDialog();

    sendAnalyticsEvent({
      gaLabel: 'More Options Back',
    });
  };

  const handleOnClose = () => {
    onClose();

    /**
     * The following timeout is necessary to account for the time it takes for the dialog to
     * animate out before resetting it. If the dialog is reset too quickly, it will appear
     * to "snap" back to its default state before the animation is complete.
     * By waiting for the animation to finish before resetting the dialog,
     * we ensure that the reset happens smoothly and reducing any visual glitches.
     */
    setTimeout(() => {
      resetDialog();
    }, transitionDuration);

    sendAnalyticsEvent({
      gaLabel: 'moreinfo - Close cross',
    });
  };

  return (
    <StyledModal
      title={activeDialogHeading}
      open={open}
      dataTestId="verypay_dialog"
      onClose={handleOnClose}
      srLabelBackButton="verypay_dialog_back_button"
      srLabelCloseButton="close very pay"
      hasBackButton={showDialogBackButton}
      hasPadding={false}
      backCallback={handleDialogBackButtonClick}
      zIndex="20"
    >
      <CSSTransition
        in={activePage.paymentOptions}
        timeout={transitionDuration}
        onExited={(element: HTMLElement) => {
          element.scrollTop = 0;
        }}
      >
        <PaymentOptionsPage
          apr={apr}
          bnpl={bnpl}
          stc={stc}
          take3={take3}
          nonCreditAccountUser={nonCreditAccountUser}
          setActivePage={setPage}
        />
      </CSSTransition>

      {bnpl && (
        <BnplPage
          active={activePage.bnpl}
          apr={apr}
          bnplAttributes={bnpl.attributes}
          nonCreditAccountUser={nonCreditAccountUser}
          transitionDuration={transitionDuration}
        />
      )}

      {take3 && (
        <Take3Page
          active={activePage.take3}
          apr={apr}
          firstPayment={take3.attributes.firstPayment}
          secondPayment={take3.attributes.secondPayment}
          thirdPayment={take3.attributes.thirdPayment}
          nonCreditAccountUser={nonCreditAccountUser}
          productPrice={productPrice}
          transitionDuration={transitionDuration}
        />
      )}

      {stc && (
        <StcPage
          active={activePage.stc}
          apr={apr}
          amount={stc.attributes.amount}
          percent={stc.attributes.percent}
          nonCreditAccountUser={nonCreditAccountUser}
          transitionDuration={transitionDuration}
        />
      )}
    </StyledModal>
  );
}

export default VeryPayDialog;
